// Navigation — the shared object-navigation vocabulary, pure.
//
// The object-based work model (flattened-object-model-design.md: workspace ›
// flat pool of {materials, sources, packages, conversations}) needs ONE place
// where the addressable work objects and the author's working memory (the
// open-resource tabs) are named. This module is that neutral home — a peer
// like `Grounding/types.ts`, owned by neither the rail nor the tab strip:
// NavRail, ResourceTabs, WorkspaceHome and the Chat context chip all import
// from here, so a resource reference means the same thing on every surface.
//
// The spatial rule the vocabulary serves: LEFT orients and selects the work
// object, CENTRE shows the active object, RIGHT explains and reviews it. The
// tabs are the author's WORKING MEMORY across the workspace's flat pool — open
// resources, not everything the workspace contains. The workspace's landing
// surface (Home) is the RESTING active state (`home`), modelled structurally
// (it always exists and cannot be closed), so "no landing" is unrepresentable
// rather than guarded. It renders as the calm empty landing in the CENTRE, not
// as a pinned strip chip, so the strip shows only the open resources +
// trailing work tabs.
//
// Discipline (dev-ts): every varying shape is a named discriminated union on a
// `type` discriminant; the folds are total, pure, and return fresh values; no
// meaning-swapping boolean on any public surface (dirty is a property OF a
// tab, not a mode). Ids are pass-through labels minted upstream (like
// `MaterialId` in app/ports.ts) — the UI never constructs one, so no brand.

import type * as Core from "@bilda/core";
import { exhaustSwitchCase } from "@bilda/utils";
import type { SearchFacet } from "../Search/searchModel.js";

// === Identity =================================================================
// Opaque pass-through labels. Deliberately `string` so mock fixtures (2-value
// worlds) and a real backend (arbitrary uuids) implement the same seam.
export type WorkspaceId = string;
export type ResourceId = string;
export type ConversationId = string;
// Restore key for view-state written before conversation tabs became id-keyed.
export const LEGACY_AMBIENT_CONVERSATION_ID: ConversationId = "ambient";

// === Work objects =============================================================
// A reference to one addressable resource — the unit the left panel lists, a
// tab holds, and the chat pins to. Each resource KIND is its own named variant
// (source: reading/evidence; material: authoring; package: export/validation)
// so a surface that treats kinds differently folds over the discriminant
// instead of branching on a string field. Future kinds extend the sum — every
// match site breaks until it handles them, which is the point.
export type SourceRef = {
    readonly type: "source";
    readonly id: ResourceId;
    readonly title: string;
};
export type MaterialRef = {
    readonly type: "material";
    readonly id: ResourceId;
    readonly title: string;
    // Authored by the agent (FR-2.7): shown with the AI mark. Absent = human.
    readonly aiAuthored?: true;
    // The material this one is a part of (a course's module → the course),
    // from the library index. Opening a part opens its course at the part
    // (bd-lya). Absent = a top-level material (or not known here).
    readonly partOf?: PartOfRef;
};
export type PartOfRef = {
    readonly rootId: ResourceId;
    readonly title: string;
};
export type ExportRef = {
    readonly type: "export";
    readonly id: ResourceId;
    readonly title: string;
};
// A raw File (pre-source upload). It has a library home (a metadata detail)
// and the shared lifecycle menu; it can be promoted to a Source.
export type FileRef = {
    readonly type: "file";
    readonly id: ResourceId;
    readonly title: string;
};
export type ResourceRef = SourceRef | MaterialRef | ExportRef | FileRef;
export type ResourceKind = ResourceRef["type"];

// The rail's fixed category set: the flat pool's three object kinds plus
// conversations. Closed union; a fourth category is an owner decision.
export type RailCategory =
    | "sources"
    | "materials"
    | "packages"
    | "conversations";

export type ConversationRailItem = {
    readonly id: string;
    readonly title: string;
};

// A resource kind's owning category, used to select the right panel when a
// rename/lifecycle action needs the row visible.
export const railCategoryForResourceKind = (
    kind: ResourceKind,
): "sources" | "materials" | "packages" =>
    kind === "source"
        ? "sources"
        : kind === "material"
          ? "materials"
          : "packages";

// A thin CLIENT-side focus hint that rides ALONGSIDE a ResourceRef when
// opening it: WHICH span within the resource to reveal (a standoff
// Selector) and, optionally, the quoted text that named it. It carries no
// domain weight — the durable anchor lives in core (`Core.Anchor`); this
// is only the transient "open here" instruction the panel resolves at the
// render edge (Phase 2/3 of the openRef cluster). Every field optional so
// a bare `openRef(ref)` needs no target at all.
//
// `lastPage` closes a PAGE SPAN that starts at the selector's page (a tool
// read pages 12-15): `Core.Anchor` has no page-range selector, so the span's
// end rides here as a client-only hint (`pageFocusTarget` / `focusPagesOf`).
export type FocusTarget = {
    readonly selector?: Core.Anchor.Selector;
    readonly quote?: string;
    readonly lastPage?: number;
};

// A 1-based inclusive page span to open a paginated source at (its `first`
// page) and visibly mark (every page through `last`).
export type PageSpan = { readonly first: number; readonly last: number };

// === Tabs — the author's working memory ======================================
// There is NO pinned home tab (owner decision 2026-08-24) and NO separate hero
// tab kind (hero-is-initial-conversation-state, 2026-08-31): the WorkspaceLanding
// hero composer is now the EMPTY visual state of a CONVERSATION tab, derived from
// the session's transcript length, never stored. Every Cmd+T / "+" opens a fresh
// (nascent, empty) conversation, closing the last tab of any kind opens a fresh
// conversation (never zero tabs), and submitting the first message is a layout
// transition on the SAME conversation tab (no tab-kind swap).
// `open` holds only resource tabs, in open order. `dirty` marks unsaved changes
// and must be visible in the strip; closing a dirty tab is GUARDED (see
// `closeTab` / `dirtyTabs`).
//
// A CONVERSATION is a real, id-keyed TabSet member (composition contract
// D3/I8): several conversations can be open, cycle, activate, close and
// persist exactly like resources. A nascent (never-sent) conversation is an
// ordinary member too — its id is client-minted from the moment the tab opens,
// and stays that same id once the first send makes it durable. An active
// conversation implies an open one, maintained by the folds below. Conversation
// tabs are never dirty and close without a guard — the conversation lives
// server-side once durable, so closing loses nothing.
export type ResourceTab = {
    readonly type: "resource";
    readonly resource: ResourceRef;
    readonly dirty: boolean;
    // WHERE in the material to open (a review item's element); session-only,
    // dropped on reload by `serialize`. Each open replaces it.
    readonly target?: FocusTarget;
};

// A full-quality report tab, scoped to the material it reports on (owner
// decision: "full quality borde vara en egen flik" — its own tab, not a modal).
// A COLLECTION member like `open` (one report per material, several can be open
// at once), NOT a singleton like settings. It is NOT
// a library resource — it projects a material's `PolicyEvaluation` as a tab — so
// it carries the material's id plus its title (the strip labels it
// "Quality: <title>"; the evaluation is loaded data the shell sources by
// materialId, never carried on the tab). Never dirty (a report holds no unsaved
// document), so its close is unguarded. MVP shortcut: quality tabs are
// SESSION-ONLY — not persisted across reload (paneTree.ts `serPane` filters
// them, an "ephemeral pass-through" treatment) — the author
// reopens from the material's quality panel.
export type QualityTab = {
    readonly type: "quality";
    readonly materialId: ResourceId;
    readonly title: string;
};

// Which tab is active — one open resource by id, or an open conversation
// (nascent or durable). A sum, not `"conversation" | ResourceId`, so the
// sentinels can never collide with an id.
export type ActiveResource = {
    readonly type: "resource";
    readonly id: ResourceId;
};
export type ActiveConversation = {
    readonly type: "conversation";
    readonly id: ConversationId;
};
// Settings is a SINGLETON tab (everything-is-a-tab, IDD 7e368440): opening it
// focuses the one tab, never a duplicate. Like the conversation it is not a
// library resource — it sits in the working set, activates and closes like a
// tab, and is never dirty.
export type ActiveSettings = { readonly type: "settings" };
// Home is a SINGLETON tab too (dogfood F1, 2026-09-17): the workspace-overview
// surface (counts per category, recent activity, the FULL semantic summary +
// the freshness thesis), distinct from a nascent conversation's hero composer.
// It follows the Settings pattern member-for-member — one tab, opened/
// re-activated by the rail's Home icon, never a library resource, never dirty.
type ActiveHome = { readonly type: "home" };
// Studio is a SINGLETON tab too (Studio v0, 2026-10-19): the canvas over the
// workspace's sources and materials. The Home pattern member-for-member.
type ActiveStudio = { readonly type: "studio" };
// Projects is a SINGLETON tab too (#161, 2026-10-21): the list of the
// author's projects; opening one goes to the Studio. The Home pattern
// member-for-member.
type ActiveProjects = { readonly type: "projects" };
// The Knowledge bank is a SINGLETON tab too (#60, 2026-10-19): the
// workspace's sources (and later what the agent knows). `selectedId` is the
// source gone into (its viewer, `/knowledge/{id}`), `target` where in it to
// open (a cited page); absent ⇒ the list.
type ActiveKnowledge = {
    readonly type: "knowledge";
    readonly selectedId?: ResourceId;
    readonly target?: FocusTarget;
};
// What a Knowledge bank tab carries besides its type.
export type KnowledgeSeed = Omit<ActiveKnowledge, "type">;
// Search is a SINGLETON tab too (dogfood F6, 2026-09-17): the workspace-wide
// Search + Recent/browse surface, promoted from an overlay to a full-page
// centre tab. Same Settings/Home pattern — one tab, opened/re-activated by the
// rail's Search + Recent doors, never a library resource, never dirty.
// `kind` is the OPTIONAL pre-selected facet (chrome-shell F33/rail-categories):
// opening Search from "Källor"/"Material"/"Paket"/"Konversationer" seeds the
// matching facet chip pre-toggled, so a category click reads as "browse this
// kind" rather than a generic search. Absent ⇒ unfiltered (the existing ⌘K /
// rail-Search behaviour, unchanged). `selectedId` is the OPTIONAL pre-selected
// row (F88): a source-open routes to `open-search {kind:"source", selectedId}`
// so the source lands SELECTED in the library detail (its F84 home), the
// retired full-page source flik's replacement. B4: it is also the LIVE
// selection (`select-library-row` writes it; the URL `/library/{id}` mirrors it).
// `target` (transient, not URL-mirrored) is WHERE in the selected source to
// open (e.g. a page a chat tool read); a new selection drops it.
// `query` is the library's LIVE query text (library search 2026-10-01: the
// text is the single source of truth, mirrored to `?q=`); absent ⇒ never typed
// since the tab opened, so the view seeds it from `kind`.
type ActiveSearch = {
    readonly type: "search";
    readonly kind?: SearchFacet;
    readonly selectedId?: ResourceId;
    readonly target?: FocusTarget;
    readonly query?: string;
};
// What a Search tab carries besides its type (the open-search seed).
export type SearchSeed = Omit<ActiveSearch, "type">;
// The active FULL-QUALITY report tab, addressed by the material it reports on
// (per-material, so `materialId` — not a bare sentinel like the singletons).
export type ActiveQuality = {
    readonly type: "quality";
    readonly materialId: ResourceId;
};
export type ActiveTab =
    | ActiveResource
    | ActiveConversation
    | ActiveSettings
    | ActiveHome
    | ActiveStudio
    | ActiveProjects
    | ActiveKnowledge
    | ActiveSearch
    | ActiveQuality;

export type TabSet = {
    readonly open: readonly ResourceTab[];
    // The open full-quality report tabs (one per material, open order). A
    // COLLECTION — the quality-report sibling of `open`, not a boolean like the
    // singletons — so several materials' reports can sit in the strip at once.
    readonly qualityOpen: readonly QualityTab[];
    // The open conversation tabs, nascent or durable, in open order. A nascent
    // (never-sent) conversation lives here exactly like a durable one — the
    // hero-is-empty-conversation model gives it a client-minted id from the
    // moment it opens (no parallel `heroesOpen` collection).
    readonly conversationsOpen: readonly ConversationId[];
    // Whether the Settings singleton tab is open — the sibling of
    // the conversation collection (rendered after the open resources;
    // settings follows it).
    readonly settingsOpen: boolean;
    // Whether the Home overview singleton tab is open — the Settings sibling
    // (dogfood F1). Rendered leading the strip (the workspace's landing chip).
    readonly homeOpen: boolean;
    // Whether the Studio canvas singleton tab is open — the Home sibling.
    readonly studioOpen: boolean;
    // Whether the Projects list singleton tab is open — the Home sibling.
    readonly projectsOpen: boolean;
    // Whether the Knowledge bank singleton tab is open — the Home sibling.
    readonly knowledgeOpen: boolean;
    // Whether the Search + Recent/browse singleton tab is open — the Home
    // sibling (dogfood F6). Rendered after Home, before the resources.
    readonly searchOpen: boolean;
    readonly active: ActiveTab;
};

// A nascent conversation's SEED id, derived deterministically from the open
// conversation ids — `conversation-1`, `conversation-2`, … Pure (no minting, no
// random): the max numeric suffix + 1, so a fresh seed never collides with an
// open seed. Used only for the STRUCTURAL "never-empty" seeds (cold-open,
// close-last, an empty reconcile); the explicit new-conversation gestures (⌘T /
// "+" / source→chat) mint a UUID in the impure shell (§3.5).
// STATED SHORTCUT (hero-is-initial-conversation-state §3.1): a deterministic
// seed that later becomes durable can, in rare multi-session cases, collide with
// an existing conversation — the app layer's `StartConversationAlreadyExists` is
// the backstop; UUIDs cover the common explicit-open gestures.
const CONVERSATION_SEED_PREFIX = "conversation-";
export const nextConversationId = (
    conversationsOpen: readonly ConversationId[],
): ConversationId => {
    const max = conversationsOpen.reduce((m, id) => {
        const n = id.startsWith(CONVERSATION_SEED_PREFIX)
            ? Number(id.slice(CONVERSATION_SEED_PREFIX.length))
            : Number.NaN;
        return Number.isFinite(n) && n > m ? n : m;
    }, 0);
    return `${CONVERSATION_SEED_PREFIX}${max + 1}`;
};

// The app's cold-open resting state: one fresh (nascent, empty) conversation —
// the calm hero composer, which is now just an empty conversation's rendering.
const COLD_OPEN_CONVERSATION_ID = nextConversationId([]);
export const emptyTabs: TabSet = {
    open: [],
    qualityOpen: [],
    conversationsOpen: [COLD_OPEN_CONVERSATION_ID],
    settingsOpen: false,
    homeOpen: false,
    studioOpen: false,
    projectsOpen: false,
    knowledgeOpen: false,
    searchOpen: false,
    active: { type: "conversation", id: COLD_OPEN_CONVERSATION_ID },
};

// === Pure folds ===============================================================

// Land the active pointer on `active`. A plain active write — a navigate-away
// closes nothing.
const withActive = (tabs: TabSet, active: ActiveTab): TabSet => ({
    ...tabs,
    active,
});

// Whether the working set holds NO tabs at all — every collection empty and no
// singleton open. The trigger for "close-last → fresh conversation": when a
// close would leave nothing, `ensureNonEmpty` conjures a fresh nascent
// conversation so the strip is never empty (never zero tabs).
const isEmptyTabs = (tabs: TabSet): boolean =>
    tabs.open.length === 0 &&
    tabs.qualityOpen.length === 0 &&
    tabs.conversationsOpen.length === 0 &&
    !tabs.settingsOpen &&
    !tabs.homeOpen &&
    !tabs.studioOpen &&
    !tabs.projectsOpen &&
    !tabs.knowledgeOpen &&
    !tabs.searchOpen;

// Guarantee the working set is non-empty: an empty set gains one fresh nascent
// conversation, active. Every close/reconcile path funnels through here (via
// `reseat`), so "closing the last tab opens a fresh conversation" holds in ONE
// place. `freshId` is a SHELL-MINTED UUID (§3.5 — ids never in the pure fold):
// it MUST be genuinely new, not the deterministic `nextConversationId` seed,
// because a deterministic seed collides with a same-named DURABLE conversation
// (e.g. a prior session's `conversation-1`), and the shell would then load that
// conversation's stale transcript instead of showing an empty hero. Threading
// the fresh id from the shell is what makes the re-seeded conversation
// genuinely new rather than a resurrected pre-existing one.
const ensureNonEmpty = (tabs: TabSet, freshId: ConversationId): TabSet => {
    if (!isEmptyTabs(tabs)) return tabs;
    return {
        ...tabs,
        conversationsOpen: [freshId],
        active: { type: "conversation", id: freshId },
    };
};

// The best remaining active tab when the active one is going away and its own
// collection has no neighbour left: a resource, then a conversation, then
// settings, then a quality report. `null` only when the set is now empty (the
// caller's `ensureNonEmpty` then seeds a fresh conversation).
const globalFallback = (tabs: TabSet): ActiveTab | null => {
    const r = tabs.open.at(-1);
    if (r !== undefined) return { type: "resource", id: r.resource.id };
    const c = tabs.conversationsOpen.at(-1);
    if (c !== undefined) return { type: "conversation", id: c };
    if (tabs.settingsOpen) return { type: "settings" };
    if (tabs.homeOpen) return { type: "home" };
    if (tabs.studioOpen) return { type: "studio" };
    if (tabs.projectsOpen) return { type: "projects" };
    if (tabs.knowledgeOpen) return { type: "knowledge" };
    if (tabs.searchOpen) return { type: "search" };
    const q = tabs.qualityOpen.at(-1);
    if (q !== undefined) return { type: "quality", materialId: q.materialId };
    return null;
};

// Re-seat `active` after the tab it pointed at was removed and its own
// collection is exhausted: fall to the best remaining tab, or seed a fresh hero
// (the shell-minted `freshId`) when the set is now empty.
const reseat = (tabs: TabSet, freshId: ConversationId): TabSet => {
    const fallback = globalFallback(tabs);
    return fallback === null
        ? ensureNonEmpty(tabs, freshId)
        : { ...tabs, active: fallback };
};

// Open a resource into the working set. If it is already open the EXISTING tab
// is activated (never a duplicate — the left panel stays the explorer, the
// strip stays the working set); otherwise it is appended clean and activated.
// `target` (where in it to open) replaces the tab's previous one; a bare open
// of an already-open, untargeted tab leaves `open` untouched.
export const openResource = (
    tabs: TabSet,
    resource: ResourceRef,
    target?: FocusTarget,
): TabSet => {
    const existing = tabs.open.find((t) => t.resource.id === resource.id);
    return {
        ...withActive(tabs, { type: "resource", id: resource.id }),
        open:
            existing === undefined
                ? [
                      ...tabs.open,
                      { type: "resource", resource, dirty: false, target },
                  ]
                : existing.target === undefined && target === undefined
                  ? tabs.open
                  : tabs.open.map((t) =>
                        t === existing ? { ...t, target } : t,
                    ),
    };
};

// Open the conversation's tab (or re-activate the already-open one) — the
// conversation-side sibling of `openResource`, same never-a-duplicate rule.
export const openConversation = (tabs: TabSet, id: ConversationId): TabSet => ({
    ...withActive(tabs, { type: "conversation", id }),
    conversationsOpen: tabs.conversationsOpen.includes(id)
        ? tabs.conversationsOpen
        : [...tabs.conversationsOpen, id],
});

// Close the conversation's tab. Never guarded (nothing unsaved lives in the
// tab); an active conversation hands focus to its neighbour (previous
// conversation, else the global fallback / a fresh hero). Closing an
// already-closed one is a no-op, same reference.
export const closeConversation = (
    tabs: TabSet,
    id: ConversationId,
    freshId: ConversationId,
): TabSet => {
    const index = tabs.conversationsOpen.indexOf(id);
    if (index === -1) return tabs;
    const conversationsOpen = tabs.conversationsOpen.filter((x) => x !== id);
    const withCollection = { ...tabs, conversationsOpen };
    if (!(tabs.active.type === "conversation" && tabs.active.id === id))
        return withCollection;
    const neighbour = conversationsOpen[index - 1] ?? conversationsOpen[index];
    return neighbour === undefined
        ? reseat(withCollection, freshId)
        : {
              ...withCollection,
              active: { type: "conversation", id: neighbour },
          };
};

// Move focus off an ACTIVE conversation tab without closing it — the tab
// stays in the strip as a parked member. A no-op (same reference) when the
// conversation is not the active tab.
export const deactivateConversation = (tabs: TabSet): TabSet =>
    tabs.active.type === "conversation"
        ? { ...tabs, active: globalFallbackOrFreshConversation(tabs) }
        : tabs;

// The global fallback as a total function (never null): used where the caller
// needs a concrete active and there is always at least the parked conversation
// left. Falls back to a fresh (unadded) conversation id only in the
// impossible-empty case, which `deactivateConversation`'s guard already excludes.
const globalFallbackOrFreshConversation = (tabs: TabSet): ActiveTab =>
    globalFallback(tabs) ?? {
        type: "conversation",
        id: nextConversationId(tabs.conversationsOpen),
    };

// Open the Settings singleton tab (or re-activate the already-open one) — the
// settings-side sibling of `openConversation`, same never-a-duplicate rule.
export const openSettings = (tabs: TabSet): TabSet => ({
    ...withActive(tabs, { type: "settings" }),
    settingsOpen: true,
});

// Close the Settings tab. Never guarded (settings holds no unsaved document);
// an active settings tab hands focus to the global fallback (or a fresh hero).
// Closing an already-closed one is a no-op, same reference.
export const closeSettings = (tabs: TabSet, freshId: ConversationId): TabSet =>
    tabs.settingsOpen
        ? tabs.active.type === "settings"
            ? reseat({ ...tabs, settingsOpen: false }, freshId)
            : { ...tabs, settingsOpen: false }
        : tabs;

// Open the Home overview singleton tab (or re-activate the already-open one) —
// the Settings sibling (dogfood F1), same never-a-duplicate rule.
export const openHome = (tabs: TabSet): TabSet => ({
    ...withActive(tabs, { type: "home" }),
    homeOpen: true,
});

// Close the Home tab. Never guarded (Home holds no unsaved document); an active
// Home tab hands focus to the global fallback (or a fresh hero). Closing an
// already-closed one is a no-op, same reference.
export const closeHome = (tabs: TabSet, freshId: ConversationId): TabSet =>
    tabs.homeOpen
        ? tabs.active.type === "home"
            ? reseat({ ...tabs, homeOpen: false }, freshId)
            : { ...tabs, homeOpen: false }
        : tabs;

// Open the Studio canvas singleton tab (or re-activate the already-open one) —
// the Home sibling, same never-a-duplicate rule.
export const openStudio = (tabs: TabSet): TabSet => ({
    ...withActive(tabs, { type: "studio" }),
    studioOpen: true,
});

// Close the Studio tab. Never guarded (its layout is device view state, saved
// as it changes); an active Studio tab hands focus to the global fallback.
export const closeStudio = (tabs: TabSet, freshId: ConversationId): TabSet =>
    tabs.studioOpen
        ? tabs.active.type === "studio"
            ? reseat({ ...tabs, studioOpen: false }, freshId)
            : { ...tabs, studioOpen: false }
        : tabs;

// Open the Projects list singleton tab (or re-activate the already-open
// one) — the Home sibling, same never-a-duplicate rule.
export const openProjects = (tabs: TabSet): TabSet => ({
    ...withActive(tabs, { type: "projects" }),
    projectsOpen: true,
});

// Close the Projects tab. Never guarded (it holds nothing unsaved); an active
// Projects tab hands focus to the global fallback.
export const closeProjects = (tabs: TabSet, freshId: ConversationId): TabSet =>
    tabs.projectsOpen
        ? tabs.active.type === "projects"
            ? reseat({ ...tabs, projectsOpen: false }, freshId)
            : { ...tabs, projectsOpen: false }
        : tabs;

// Open the Knowledge bank singleton tab (or re-activate the already-open
// one) at exactly `seed`: a source (its viewer) or, with no `selectedId`, the
// list. Never a duplicate.
export const openKnowledge = (
    tabs: TabSet,
    seed: KnowledgeSeed = {},
): TabSet => ({
    ...withActive(tabs, { type: "knowledge", ...seed }),
    knowledgeOpen: true,
});

// Close the Knowledge bank tab. Never guarded (it holds nothing unsaved).
export const closeKnowledge = (
    tabs: TabSet,
    freshId: ConversationId,
): TabSet =>
    tabs.knowledgeOpen
        ? tabs.active.type === "knowledge"
            ? reseat({ ...tabs, knowledgeOpen: false }, freshId)
            : { ...tabs, knowledgeOpen: false }
        : tabs;

// Open the Search + Recent singleton tab (or re-activate the already-open one)
// — the Home sibling (dogfood F6), same never-a-duplicate rule. `kind` seeds
// the pre-selected facet (see ActiveSearch); omitted ⇒ unfiltered, exactly the
// prior behaviour. `selectedId` seeds the pre-selected row (F88 source-open);
// `target` where in it to open.
export const openSearch = (tabs: TabSet, seed: SearchSeed = {}): TabSet => ({
    ...withActive(tabs, { type: "search", ...seed }),
    searchOpen: true,
});

// Record the library's opened row on the ACTIVE Search tab (B4: the URL
// `/library/{id}` mirrors it), with where in it to open (a content hit's
// page). A no-op (same reference) when the active tab is not Search or
// nothing changes.
export const selectSearchRow = (
    tabs: TabSet,
    selectedId?: ResourceId,
    target?: FocusTarget,
): TabSet =>
    tabs.active.type === "search" &&
    (tabs.active.selectedId !== selectedId || target !== undefined)
        ? { ...tabs, active: { ...tabs.active, selectedId, target } }
        : tabs;

// Record the library's live query text on the ACTIVE Search tab (mirrored to
// `?q=`). A no-op (same reference) when not Search or unchanged.
export const setSearchQuery = (tabs: TabSet, query: string): TabSet =>
    tabs.active.type === "search" && tabs.active.query !== query
        ? { ...tabs, active: { ...tabs.active, query } }
        : tabs;

// Close the Search tab. Never guarded (Search holds no unsaved document); an
// active Search tab hands focus to the global fallback (or a fresh hero).
// Closing an already-closed one is a no-op, same reference.
export const closeSearch = (tabs: TabSet, freshId: ConversationId): TabSet =>
    tabs.searchOpen
        ? tabs.active.type === "search"
            ? reseat({ ...tabs, searchOpen: false }, freshId)
            : { ...tabs, searchOpen: false }
        : tabs;

// Open (or re-activate) a material's full-quality report tab — the
// quality-report sibling of `openResource`, same never-a-duplicate rule (one
// report per material). Appended after the open resources; landing here is a
// plain navigate-away.
export const openQuality = (tabs: TabSet, tab: QualityTab): TabSet => {
    const existing = tabs.qualityOpen.some(
        (q) => q.materialId === tab.materialId,
    );
    return {
        ...withActive(tabs, { type: "quality", materialId: tab.materialId }),
        qualityOpen: existing ? tabs.qualityOpen : [...tabs.qualityOpen, tab],
    };
};

// Close one material's quality report. Never guarded (a report holds nothing
// unsaved). When the closed tab was active, focus falls to its neighbour among
// the quality tabs (previous, else next), else to the last resource / home. A
// no-op (same reference) when no such quality tab is open.
export const closeQuality = (
    tabs: TabSet,
    materialId: ResourceId,
    freshId: ConversationId,
): TabSet => {
    const index = tabs.qualityOpen.findIndex(
        (q) => q.materialId === materialId,
    );
    if (index === -1) return tabs;
    const qualityOpen = tabs.qualityOpen.filter(
        (q) => q.materialId !== materialId,
    );
    const isActive =
        tabs.active.type === "quality" && tabs.active.materialId === materialId;
    if (!isActive) return { ...tabs, qualityOpen };
    const neighbour = qualityOpen[index - 1] ?? qualityOpen[index];
    return neighbour !== undefined
        ? {
              ...tabs,
              qualityOpen,
              active: { type: "quality", materialId: neighbour.materialId },
          }
        : reseat({ ...tabs, qualityOpen }, freshId);
};

// Activate a tab that is already in the set (the strip's click). Activating a
// resource that is not open — or the conversation while its tab is closed —
// is a no-op rather than an invention: the strip only ever offers tabs that
// exist.
export const activateTab = (tabs: TabSet, active: ActiveTab): TabSet => {
    switch (active.type) {
        case "resource":
            return tabs.open.some((t) => t.resource.id === active.id)
                ? withActive(tabs, active)
                : tabs;
        case "conversation":
            return tabs.conversationsOpen.includes(active.id)
                ? withActive(tabs, active)
                : tabs;
        case "settings":
            return tabs.settingsOpen ? withActive(tabs, active) : tabs;
        case "home":
            return tabs.homeOpen ? withActive(tabs, active) : tabs;
        case "studio":
            return tabs.studioOpen ? withActive(tabs, active) : tabs;
        case "projects":
            return tabs.projectsOpen ? withActive(tabs, active) : tabs;
        case "knowledge":
            return tabs.knowledgeOpen ? withActive(tabs, active) : tabs;
        case "search":
            return tabs.searchOpen ? withActive(tabs, active) : tabs;
        case "quality":
            return tabs.qualityOpen.some(
                (q) => q.materialId === active.materialId,
            )
                ? withActive(tabs, active)
                : tabs;
        default:
            return exhaustSwitchCase(active);
    }
};

// Closing a tab is GUARDED: a clean tab closes; a dirty tab does not close by
// itself — the fold reports the tab so the shell can confirm the discard.
// A sum, so "closed" and "blocked on unsaved changes" cannot be conflated.
export type TabClosed = { readonly type: "closed"; readonly tabs: TabSet };
export type CloseBlocked = {
    readonly type: "unsaved";
    readonly tab: ResourceTab;
};
export type CloseOutcome = TabClosed | CloseBlocked;

// Drop `id` from the set unconditionally. When the closed tab was active, the
// nearest surviving neighbour (previous, else next) takes over; the last
// resource tab hands focus back to home. Closing ≠ deleting — the resource
// stays in the workspace.
const dropTab = (
    tabs: TabSet,
    id: ResourceId,
    freshId: ConversationId,
): TabSet => {
    const index = tabs.open.findIndex((t) => t.resource.id === id);
    if (index === -1) return tabs;
    const open = tabs.open.filter((t) => t.resource.id !== id);
    if (tabs.active.type !== "resource" || tabs.active.id !== id) {
        return { ...tabs, open };
    }
    const neighbour = open[index - 1] ?? open[index];
    return neighbour
        ? {
              ...tabs,
              open,
              active: { type: "resource", id: neighbour.resource.id },
          }
        : reseat({ ...tabs, open }, freshId);
};

export const closeTab = (
    tabs: TabSet,
    id: ResourceId,
    freshId: ConversationId,
): CloseOutcome => {
    const tab = tabs.open.find((t) => t.resource.id === id);
    if (tab?.dirty) return { type: "unsaved", tab };
    return { type: "closed", tabs: dropTab(tabs, id, freshId) };
};

// The confirmed discard — the author chose to lose the unsaved changes. Only
// reachable AFTER `closeTab` reported `unsaved`; a separate named intent, not
// a `force` flag on `closeTab`.
export const closeTabDiscarding = (
    tabs: TabSet,
    id: ResourceId,
    freshId: ConversationId,
): TabSet => dropTab(tabs, id, freshId);

// === Batch curation — close-others / close-to-right ==========================
// The new set + the resources that were closed (so the caller remembers them on
// the reopen stack). A batch close NEVER silently discards unsaved work: a dirty
// tab is left OPEN (the author closes it deliberately, meeting the unsaved
// guard). Singletons (home/conversation/settings) are untouched — they
// are not in `open`.
export type BatchCloseResult = {
    readonly tabs: TabSet;
    readonly closed: readonly ResourceRef[];
};

// Close every OTHER clean resource tab, keeping `keepId` (which stays active).
export const closeOthers = (
    tabs: TabSet,
    keepId: ResourceId,
): BatchCloseResult => {
    const closed = tabs.open
        .filter((t) => t.resource.id !== keepId && !t.dirty)
        .map((t) => t.resource);
    if (closed.length === 0) return { tabs, closed };
    const open = tabs.open.filter((t) => t.resource.id === keepId || t.dirty);
    const active: ActiveTab = open.some((t) => t.resource.id === keepId)
        ? { type: "resource", id: keepId }
        : tabs.active;
    return { tabs: { ...tabs, open, active }, closed };
};

// Close clean resource tabs positioned AFTER `afterId` in the strip order.
export const closeToRight = (
    tabs: TabSet,
    afterId: ResourceId,
): BatchCloseResult => {
    const idx = tabs.open.findIndex((t) => t.resource.id === afterId);
    if (idx < 0) return { tabs, closed: [] };
    const closed = tabs.open
        .slice(idx + 1)
        .filter((t) => !t.dirty)
        .map((t) => t.resource);
    if (closed.length === 0) return { tabs, closed };
    const closedIds = new Set(closed.map((r) => r.id));
    const open = tabs.open.filter((t) => !closedIds.has(t.resource.id));
    const active: ActiveTab =
        tabs.active.type === "resource" && closedIds.has(tabs.active.id)
            ? { type: "resource", id: afterId }
            : tabs.active;
    return { tabs: { ...tabs, open, active }, closed };
};

// === Reconciling a restored working set ======================================
// A TabSet restored from device storage (app/viewState.ts) may reference
// resources that no longer exist — the events file can change between
// sessions — or whose titles have moved on. Once the workspace's library (the
// truth) has loaded, this fold re-establishes the invariants: each open
// tab's ref is replaced by the library's version (fresh title), tabs whose
// resource no longer exists are dropped, and an active tab that did not
// survive hands focus back to home. The conversation tab is untouched — it
// is not a library resource, so the library cannot invalidate it. Returns
// the SAME reference when nothing changed, so the caller can skip a no-op
// state write.
export const reconcileTabs = (
    tabs: TabSet,
    existing: readonly ResourceRef[],
    freshId: ConversationId,
): TabSet => {
    const byId = new Map(existing.map((r) => [r.id, r]));
    const open = tabs.open.flatMap((t): readonly ResourceTab[] => {
        const ref = byId.get(t.resource.id);
        if (ref === undefined) return [];
        const same =
            ref.type === t.resource.type && ref.title === t.resource.title;
        return [same ? t : { ...t, resource: ref }];
    });
    const staleActive =
        tabs.active.type === "resource" && !byId.has(tabs.active.id);
    const unchanged =
        !staleActive &&
        open.length === tabs.open.length &&
        open.every((t, i) => t === tabs.open[i]);
    if (unchanged) return tabs;
    // A stale active resource re-seats onto the best surviving tab (or a fresh
    // hero when the reconcile emptied the set); anything else keeps its active.
    return staleActive
        ? reseat({ ...tabs, open }, freshId)
        : { ...tabs, open, active: tabs.active };
};

// === Closed-tab history ======================================================
// The stack of recently CLOSED resources (newest first, capped) —
// the desktop "reopen closed tab" command's memory. A closed tab keeps only
// its ResourceRef: dirty state was resolved at close time (clean, or the
// confirmed discard), so reopening simply opens the resource fresh via
// `openResource`. Session-local, like the tab sets themselves.
export type ClosedTabs = readonly ResourceRef[];
export const emptyClosedTabs: ClosedTabs = [];

// Bound the stack so it stays a working-memory aid, not an unbounded log —
// the author who wants further back has the rail, not the reopen chord.
const CLOSED_TABS_CAP = 10;

// Remember a closed resource. Re-closing a resource MOVES it to the top
// rather than duplicating — one reopen per resource is the useful memory.
export const rememberClosed = (
    closed: ClosedTabs,
    resource: ResourceRef,
): ClosedTabs =>
    [resource, ...closed.filter((r) => r.id !== resource.id)].slice(
        0,
        CLOSED_TABS_CAP,
    );

// The most recently closed resource plus the stack that remains once it is
// taken — `takeLastClosed`'s ok shape.
export type ReopenedTab = {
    readonly resource: ResourceRef;
    readonly rest: ClosedTabs;
};

// Take the top of the stack, or `undefined` for an empty one (the caller
// no-ops — reopening with nothing closed is not an error).
export const takeLastClosed = (closed: ClosedTabs): ReopenedTab | undefined => {
    const [resource, ...rest] = closed;
    return resource === undefined ? undefined : { resource, rest };
};
